import React, { useState } from 'react';
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from "../ToolRenderers";
import { DataTable } from "../../ui/datatable/datatable";
import { Badge } from "../../ui/badge";
import { ToolRendererProps } from "../ToolRenderers";
import { AlertTriangle, Info } from "lucide-react";

interface K8sEvent {
  environment: string;
  latestTimestamp: number;
  uuid: string;
  version: string;
  eventType: string;
  resourceName: string;
  serviceName: string;
  namespace: string;
  reason: string;
  message: string;
  involvedObjectName: string;
  involvedObjectNamespace: string;
  involvedObjectKind: string;
  involvedObjectUUID: string;
  involvedObjectVersion: string;
  reportingComponent: string;
  reportingInstance: string;
  count: number;
}

interface K8sEventsResponse {
  k8sEvents: K8sEvent[];
}

// Function to format timestamp
const formatTimestamp = (timestamp: number): string => {
  if (!timestamp) return "";
  
  return new Date(timestamp).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function K8sEventsToolRenderer({ message, isToolCall }: ToolRendererProps) {
  // We only want to render the response, not the call
  if (!message.tool_call_response_content) {
    // This is a tool call, do the default rendering
    return <DefaultToolCallRenderer message={message} />;
  }

  let response: K8sEventsResponse | null = null;
  try {
    response = JSON.parse(message.tool_call_response_content);
  } catch (error) {
    console.error('Error parsing k8s events response data:', error);
    return null;
  }

  // Validate response
  if (!response || !response.k8sEvents || !Array.isArray(response.k8sEvents)) {
    return null;
  }

  // Define columns for the DataTable
  const columns: ColumnDef<K8sEvent>[] = [
    {
      id: "eventType",
      accessorKey: "eventType",
      header: "Type",
      size: 100,
      cell: ({ row }) => {
        const eventType = row.original.eventType;
        return (
          <div className="flex items-center">
            {eventType === "Warning" ? (
              <Badge 
                className="bg-orange-500/20 text-orange-500 hover:bg-orange-500/20 flex items-center gap-1"
              >
                <AlertTriangle className="h-3 w-3" /> 
                Warning
              </Badge>
            ) : (
              <Badge 
                className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/20 flex items-center gap-1"
              >
                <Info className="h-3 w-3" /> 
                Normal
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "reason",
      accessorKey: "reason",
      header: "Reason",
      size: 120,
      cell: ({ row }) => <div className="font-medium">{row.original.reason}</div>,
    },
    {
      id: "message",
      accessorKey: "message",
      header: "Message",
      size: 300,
      cell: ({ row }) => <div className="max-w-md whitespace-normal break-words">{row.original.message}</div>,
    },
    {
      id: "involvedObject",
      header: "Involved Object",
      size: 200,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <span className="font-medium">{row.original.involvedObjectKind}</span>
          </div>
          <div className="text-sm text-textmedium truncate" title={`${row.original.involvedObjectNamespace}/${row.original.involvedObjectName}`}>
            {row.original.involvedObjectNamespace}/{row.original.involvedObjectName}
          </div>
        </div>
      ),
    },
    {
      id: "reporting",
      header: "Reporting",
      size: 160,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="text-sm truncate" title={row.original.reportingComponent}>
            {row.original.reportingComponent}
          </div>
          <div className="text-sm text-textmedium truncate" title={row.original.reportingInstance}>
            {row.original.reportingInstance}
          </div>
        </div>
      ),
    },
    {
      id: "count",
      accessorKey: "count",
      header: "Count",
      size: 80,
      cell: ({ row }) => <div>{row.original.count}</div>,
    },
    {
      id: "environment",
      accessorKey: "environment",
      header: "Environment",
      size: 140,
      cell: ({ row }) => <div className="truncate" title={row.original.environment}>{row.original.environment}</div>,
    },
    {
      id: "latestTimestamp",
      accessorKey: "latestTimestamp",
      header: "Last Seen",
      size: 180,
      cell: ({ row }) => formatTimestamp(row.original.latestTimestamp),
    },
  ];

  // Define filters for the table
  const filters: { filteringColumn: string; title: string; }[] = [
    { filteringColumn: "eventType", title: "Event Type" },
    { filteringColumn: "involvedObjectKind", title: "Resource Kind" },
    { filteringColumn: "environment", title: "Environment" },
    { filteringColumn: "reason", title: "Reason" },
  ];

  return (
    <div className="w-full border rounded">
      <DataTable
        columns={columns}
        data={response.k8sEvents}
        tableTitle="Kubernetes Events"
        filters={filters}
        showPagination={true}
        removeBorder={true}
        textFilteringColumnName="message"
        textFilteringPlaceholder="Search event messages..."
        columnConfigs={[
          { id: "eventType", isFilterable: true },
          { id: "reason", isFilterable: true },
          { id: "involvedObjectKind", isFilterable: true },
          { id: "environment", isFilterable: true }
        ]}
      />
    </div>
  );
} 